import { registerPlugin } from '@capacitor/core'

/**
 * Reads the managed app configuration an MDM pushed to this device.
 *
 * Native implementations:
 *   - `ios/App/App/ManagedConfigPlugin.swift` — `com.apple.configuration.managed`
 *   - `android/app/src/main/java/com/digola/lingotalk/ManagedConfigPlugin.java`
 *     — `RestrictionsManager`, schema in `res/xml/app_restrictions.xml`
 *
 * Only one key so far: `org_token`, a facility's volume-licence token. See
 * `lib/org-license.ts` for what happens with it, and `docs/org-licensing.md`
 * for the admin-side setup.
 */
interface ManagedConfigBridge {
  get(): Promise<{ orgToken: string | null }>
}

const ManagedConfig = registerPlugin<ManagedConfigBridge>('ManagedConfig')

/**
 * The licence token an administrator configured, or null.
 *
 * Best-effort by design: an unmanaged device, a web build, an older bundle
 * without the native side — all of them mean "no managed token", which is the
 * ordinary case, not an error. `registerPlugin` is lazy and only fails at
 * invoke time, so the try/catch is what makes this safe to call anywhere.
 */
export async function getManagedOrgToken(): Promise<string | null> {
  try {
    const { orgToken } = await ManagedConfig.get()
    return orgToken && orgToken.trim() !== '' ? orgToken : null
  } catch {
    return null
  }
}
